// Admin tab — Mécénat / Donations management with receipt download & resend
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const STATUS_BADGE = {
  paid: { bg: "rgba(132,159,114,0.18)", fg: "var(--moss)", label: "PAYÉ" },
  unpaid: { bg: "rgba(120,113,108,0.12)", fg: "var(--muted)", label: "EN ATTENTE" },
  pending_open: { bg: "rgba(201,184,118,0.18)", fg: "var(--gold)", label: "EN ATTENTE OUV." },
};

export default function DonationsTab({ locale }) {
  const isFr = locale === "fr";
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | unpaid | pending_open
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/donations");
      setDonations(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadReceipt = async (d) => {
    try {
      const res = await api.get(`/admin/donations/${d.id}/receipt.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recu-fiscal-${d.receipt_number || d.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const resendReceipt = async (d) => {
    if (!window.confirm(isFr ? `Renvoyer le reçu fiscal à ${d.email} ?` : `Resend the tax receipt to ${d.email}?`)) return;
    try {
      await api.post(`/admin/donations/${d.id}/resend-receipt`);
      window.alert(isFr ? "Reçu renvoyé ✓" : "Receipt resent ✓");
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const downloadCsv = () => {
    const url = `${api.defaults.baseURL}/admin/donations.csv`;
    window.open(url, "_blank");
  };

  const filtered = donations.filter((d) => {
    if (filter !== "all" && d.payment_status !== filter) return false;
    if (search && !`${d.name} ${d.email} ${d.receipt_number || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPaid = donations.filter((d) => d.payment_status === "paid")
                             .reduce((s, d) => s + (d.amount || 0), 0);
  const totalPending = donations.filter((d) => d.payment_status !== "paid")
                                .reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div data-testid="donations-tab" className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card label={isFr ? "DONS COLLECTÉS" : "DONATIONS RAISED"} value={`${totalPaid.toFixed(2)} €`} color="var(--moss)" />
        <Card label={isFr ? "EN ATTENTE" : "PENDING"} value={`${totalPending.toFixed(2)} €`} color="var(--gold)" />
        <Card label={isFr ? "NOMBRE DE DONS" : "DONATION COUNT"} value={`${donations.filter((d) => d.payment_status === "paid").length}`} color="var(--ink)" />
        <Card label={isFr ? "EN ATTENTE OUV." : "AWAITING OPENING"} value={`${donations.filter((d) => d.payment_status === "pending_open").length}`} color="var(--cinnabar)" />
      </div>

      {/* Toolbar */}
      <div className="bg-white border p-4 flex flex-wrap items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <input
          data-testid="donations-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isFr ? "Recherche nom / email / n° reçu" : "Search name / email / receipt #"}
          className="input-classic"
          style={{ flex: "1 1 280px" }}
        />
        <select data-testid="donations-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                className="input-classic" style={{ minWidth: 180 }}>
          <option value="all">{isFr ? "Tous statuts" : "All statuses"}</option>
          <option value="paid">{isFr ? "Payés" : "Paid"}</option>
          <option value="unpaid">{isFr ? "En attente paiement" : "Pending payment"}</option>
          <option value="pending_open">{isFr ? "En attente d'ouverture" : "Awaiting opening"}</option>
        </select>
        <button data-testid="donations-csv" onClick={downloadCsv} className="btn btn-outline">CSV</button>
        <button data-testid="donations-reload" onClick={load} className="btn btn-outline">⟳</button>
      </div>

      {/* Table */}
      <div className="bg-white border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        <table className="w-full font-body text-sm">
          <thead>
            <tr style={{ background: "var(--parchment-2)" }}>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "DATE" : "DATE"}</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "DONATEUR" : "DONOR"}</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>EMAIL</th>
              <th className="text-right p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "MONTANT" : "AMOUNT"}</th>
              <th className="text-center p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "STATUT" : "STATUS"}</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "REÇU" : "RECEIPT"}</th>
              <th className="text-right p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center" style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun don pour l'instant." : "No donations yet."}
              </td></tr>
            )}
            {filtered.map((d) => {
              const badge = STATUS_BADGE[d.payment_status] || STATUS_BADGE.unpaid;
              return (
                <tr key={d.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                  <td className="p-3 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                    {(d.created_at || "").slice(0, 10)}
                  </td>
                  <td className="p-3">
                    <div style={{ color: "var(--ink)" }}>
                      {d.civility ? `${d.civility} ` : ""}{d.name}
                      {d.donor_type === "entreprise" && (
                        <span className="ml-2 font-caps text-[0.55rem]" style={{ color: "var(--gold)", letterSpacing: "0.15em" }}>ENTR.</span>
                      )}
                      {d.anonymous && (
                        <span className="ml-2 font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>ANON.</span>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{d.city} · {d.country}</div>
                  </td>
                  <td className="p-3 text-xs" style={{ color: "var(--ink-2)" }}>{d.email}</td>
                  <td className="p-3 text-right font-display text-lg">{d.amount?.toFixed(2)} €</td>
                  <td className="p-3 text-center">
                    <span className="font-caps text-[0.6rem] px-2 py-1 inline-block"
                          style={{ background: badge.bg, color: badge.fg, letterSpacing: "0.15em" }}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs" style={{ color: d.receipt_number ? "var(--ink)" : "var(--muted)" }}>
                    {d.receipt_number || "—"}
                    {d.receipt_sent_at && (
                      <div className="text-[10px]" style={{ color: "var(--moss)" }}>{isFr ? "Envoyé" : "Sent"} ✓</div>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {d.payment_status === "paid" && (
                      <div className="inline-flex gap-1">
                        <button data-testid={`receipt-pdf-${d.id}`} onClick={() => downloadReceipt(d)}
                                className="font-caps text-[0.6rem] px-2.5 py-1 border"
                                style={{ borderColor: "var(--gold)", color: "var(--gold)", letterSpacing: "0.15em" }}>PDF</button>
                        <button data-testid={`receipt-resend-${d.id}`} onClick={() => resendReceipt(d)}
                                className="font-caps text-[0.6rem] px-2.5 py-1 border"
                                style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
                          {isFr ? "RENVOI" : "RESEND"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, color }) {
  return (
    <div className="bg-white border p-5" style={{ borderColor: "var(--border)" }}>
      <div className="font-caps text-[0.65rem]" style={{ letterSpacing: "0.2em", color: "var(--muted)" }}>{label}</div>
      <div className="font-display text-3xl mt-2" style={{ color }}>{value}</div>
    </div>
  );
}
